import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import {
  HEADER_HEIGHT,
  INPUT_HEIGHT,
  SCROLL_CONTAINER_ID,
  TOP_OFFSET,
} from '@constants/app.constants';
import { Observable } from 'rxjs';

type ScrollToParams = {
  element?: ElementRef<HTMLDivElement>;
  smooth?: boolean;
};

export interface N0ScrollOptions {
  duration?: number;
  offsetHeight?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  private readonly scrollContainerId = SCROLL_CONTAINER_ID;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Scrolls to the given element id.
   * @param elId The element id to scroll to.
   * @param behavior The scroll behavior.
   */
  scrollToId(elId: string, behavior?: ScrollBehavior): void {
    try {
      const el = this.document.getElementById(elId);
      if (el) {
        const scrollToOptions: ScrollToOptions = {
          top: el.getBoundingClientRect().top + window.scrollY,
          behavior: behavior ?? 'smooth',
        };

        this.document
          .getElementById(this.scrollContainerId)
          ?.scrollTo(scrollToOptions);
      }
    } catch (error) {
      console.error('Error scrolling to id:', error);
    }
  }

  /**
   * Scrolls the given element to the top.
   * @param element The element to scroll to the top.
   * @param options The scroll options.
   */
  scrollByPoint(
    element: HTMLElement | null,
    behavior?: ScrollBehavior,
  ): void {
    if (!element) {
      return;
    }

    try {
      const scrollToOptions: ScrollToOptions = {
        top: this.getTopOffset(element),
        behavior: behavior ?? 'smooth',
      };

      this.document
        .getElementById(this.scrollContainerId)
        ?.scrollTo(scrollToOptions);
    } catch (error) {
      console.error('Error scrolling by point:', error);
    }
  }

  /**
   * Scrolls the given element to the top.
   *
   * @param element The element to scroll to the top.
   * @param smooth The scroll smooth behavior. Not working on iOS.
   */
  scrollElementToTop(params?: ScrollToParams): void {
    try {
      const scrollToOptions: ScrollToOptions = {
        top: 0,
        behavior: params?.smooth ? 'smooth' : 'instant',
      };

      if (params?.element) {
        params.element.nativeElement.scrollTo(scrollToOptions);
      }

      this.document
        .getElementById(this.scrollContainerId)
        ?.scrollTo(scrollToOptions);
    } catch (error) {
      console.error('Error scrolling to top:', error);
    }
  }

  /**
   * Scrolls the scroll area to the top.
   * @param smooth The scroll smooth behavior.
   */
  scrollToTop(smooth?: boolean): void {
    this.scrollElementToTop({ smooth });
  }

  /**
   * Scrolls the scroll area to the top.
   * @param controlEl The control element to scroll to.
   * @returns The top offset.
   */
  getTopOffset(controlEl?: HTMLElement): number {
    const scrollContainer = this.document.getElementById(this.scrollContainerId);
    const targetElement = controlEl ?? scrollContainer;

    if (!scrollContainer || !targetElement) {
      return 0;
    }

    const { top: elementTop } = targetElement.getBoundingClientRect();
    const { top: containerTop } = scrollContainer.getBoundingClientRect();
    const labelOffset = HEADER_HEIGHT + INPUT_HEIGHT + TOP_OFFSET;

    return elementTop - containerTop + scrollContainer.scrollTop - labelOffset;
  }

  onScrollStartObservable(): Observable<number> {
    return new Observable((observer) => {
      const scrollContainer = this.document.getElementById(
        this.scrollContainerId,
      );

      if (!scrollContainer) {
        return;
      }

      scrollContainer.addEventListener('scroll', () => {
        const fromTop = scrollContainer.scrollTop;
        observer.next(fromTop);
      });
    });
  }
}
